import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_USR_SEARCH_FORM,
    SG_USR_LIST,
    SG_USR_EXCEL_LIST,
} from "@/tra/tancis/ptli/prcsmntr/intusr/service/PtliIntUsrPrcsService";
import dayjs from "dayjs";

/*
 * Select list of internal user account processing status
 * !== 내부 사용자 계정처리현황 목록 조회 ==!
 */
export const PTLI0601001Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "PTL0001" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        usrSrch: useForm({
            defaultSchema: SF_USR_SEARCH_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
                reqTimeStart: comnUtils.getDate({ m: -1 }),
                reqTimeEnd: comnUtils.getDate(),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        usrSrchList: useGrid({
            defaultSchema: SG_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        usrExcelList: useGrid({
            defaultSchema: SG_USR_EXCEL_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get the list of internal user account processing status. !== 내부 사용자 계정처리현황 목록을 조회한다. ==!
        getIntUsrPrcsList: useFetch({
            api: (page = grid.usrSrchList.page) =>
                APIS.getIntUsrPrcsList(form.usrSrch.getValues(), page, grid.usrSrchList.size),
            enabled: comnUtils.isEmpty(form.usrSrch.errors),
            key: [grid.usrSrchList.page, grid.usrSrchList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.usrSrch.getValues(),
                    page: grid.usrSrchList.page,
                    size: grid.usrSrchList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        getIntUsrPrcsExcelList: useFetch({
            api: (data: any) => (load ? APIS.getIntUsrPrcsExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.usrSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.usrExcelList.exportExcel({
                        excelName: `${t("L_INT_PTL")}_${t("T_USER")}_${t("L_ACCT_PRCSS_STAT")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_usrSrchList: {
            cell: {
                usrNm: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Link to={`/ptli/prcsmntr/intusr/ptli0601002q/${rowValues.usrDmnId}?menuId=${menuId}`}>
                            {value}
                        </Link>
                    );
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getIntUsrPrcsList: (page: number = 0) => {
            form.usrSrch.handleSubmit(
                () => {
                    grid.usrSrchList.setPage(page);
                    fetch.getIntUsrPrcsList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.usrSrchList.setPage(0);
            fetch.getIntUsrPrcsList.fetch(0);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getIntUsrPrcsList();
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getIntUsrPrcsExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.usrSrch.schema.reqDateRnge} />
                            <Group.Control {...form.usrSrch.schema.aplyStat} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrSrch.schema.usrDmnId} onEnter={handler.getIntUsrPrcsList} />
                            <Group.Control {...form.usrSrch.schema.usrNm} onEnter={handler.getIntUsrPrcsList} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.usrSrch.schema.usrTin} onEnter={handler.getIntUsrPrcsList} />
                            <Group.Control {...form.usrSrch.schema.cstmOfcrNo} onEnter={handler.getIntUsrPrcsList} />
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.usrSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getIntUsrPrcsList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>

                    <Group.Section>
                        <Grid
                            {...grid.usrSrchList.grid}
                            data={fetch.getIntUsrPrcsList.data?.intUsrList}
                            render={render.grid_usrSrchList}
                        />
                    </Group.Section>

                    <Group hidden>
                        <Grid {...grid.usrExcelList.grid} data={fetch.getIntUsrPrcsExcelList.data?.intUsrExcelList} />
                    </Group>
                </Group.Body>
            </Group>
        </Page>
    );
};
